import { cva, cx } from "class-variance-authority";
import Image from "next/image";
import type { PropsWithChildren } from "react";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { CloseIcon } from "@/assets/icons/general/Close";
import ModalCoverImage from "@/assets/images/modal-cover.png";
import { InlineIconButton } from "@/components/ui/buttons/InlineIconButton/InlineIconButton";
import { Typography } from "@/components/ui/text/Typography/Typography";

const modalContent = cva(
  ["relative flex w-full flex-col items-center px-4 t:px-9 t:py-6", "bg-secondary-300 shadow-5 outline-hidden"],
  {
    variants: {
      aside: {
        left: "h-[100svh] rounded-none",
        right: "h-[100svh] rounded-none",
        center: "max-h-[100svh] dm:rounded-none t:rounded-[18px]",
        bounce: "max-h-[100svh] dm:rounded-none t:rounded-[18px]",
        transparent: "bg-transparent shadow-[none]",
        chat: "dm:rounded-none t:rounded-[18px]",
      },
    },
    defaultVariants: {
      aside: "center",
    },
  },
);

const modalOverlay = cva(
  ["fixed inset-0 z-30 flex w-screen dm:items-end t:items-center bg-secondary-200/80 backdrop-blur-2", "h-[100svh]"],
  {
    variants: {
      aside: {
        left: "justify-start p-0",
        right: "justify-end p-0",
        center: "dm:items-center justify-center dm:p-1 t:p-4",
        bounce: "justify-center dm:p-0 t:p-4",
        transparent: "dm:items-center justify-center dm:p-0 t:p-4",
        chat: "h-[--visual-viewport-height] justify-center p-0 transition-all duration-75",
      },
    },
    defaultVariants: {
      aside: "center",
    },
  },
);

const modalMain = cva(["w-auto max-w-modal"], {
  variants: {
    aside: {
      left: "entering:animate-drawer-enter-left exiting:animate-drawer-exit-left",
      right:
        "fixed top-0 bottom-0 dm:w-full t:w-auto entering:animate-drawer-enter-right exiting:animate-drawer-exit-right",
      center: "",
      transparent: "",
      chat: "",
      bounce: "dm:max-w-[100vw] t:max-w-[80vw] entering:animate-overlay-bounce-in exiting:animate-overlay-bounce-out",
    },
  },
  defaultVariants: {
    aside: "center",
  },
});

export type ModalProps = {
  className?: string;
  isOpen: boolean;
  portalContainerRef?: React.RefObject<HTMLElement>;
  onClose: () => void;
  aside?: "left" | "right" | "transparent" | "chat" | "bounce";
  title?: string | React.ReactNode;
  showCoverImage?: boolean;
  hideCloseButton?: boolean;
  dialogClassName?: string;
  modalClassName?: string;
  "data-testid"?: string;
};

export const Modal = ({
  isOpen,
  portalContainerRef,
  onClose,
  aside,
  children,
  title,
  showCoverImage,
  className,
  dialogClassName,
  modalClassName,
  hideCloseButton,
  "data-testid": dataTestId,
}: PropsWithChildren<ModalProps>) => {
  const { t } = useTranslation();

  return (
    <AriaModalOverlay
      UNSTABLE_portalContainer={portalContainerRef?.current || undefined}
      className={cx(modalOverlay({ aside }), className)}
      isDismissable
      isOpen={isOpen}
      shouldCloseOnInteractOutside={() => {
        return !portalContainerRef?.current;
      }}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <AriaModal className={cx(modalMain({ aside }), modalClassName)}>
        <AriaDialog
          className={cx(modalContent({ aside }), showCoverImage ? "relative" : "", dialogClassName)}
          aria-label="modal"
          data-testid={dataTestId}
        >
          {showCoverImage && (
            <div className="absolute top-0 right-0 left-0 z-0 h-[142px] w-full">
              <Image
                src={ModalCoverImage}
                alt="Modal cover"
                fill
                className="object-cover"
              />
            </div>
          )}
          {(!hideCloseButton || title) && (
            <div className="relative z-50 dm:m-6 t:m-0 flex w-full items-center justify-center">
              {!hideCloseButton && (
                <InlineIconButton
                  onPress={onClose}
                  icon={CloseIcon}
                  className="absolute t:top-0 right-0 t:right-4 z-10 p-0"
                  iconClassName="h-6 w-6 text-white"
                  label={t("ui.modal.closeBtn")}
                />
              )}
              {title && (
                <Typography
                  variant="prominent-2"
                  size="h5"
                  className="top-2 right-0 left-0 t:max-w-md px-9 text-center font-weight-600"
                >
                  {title}
                </Typography>
              )}
            </div>
          )}
          {children}
        </AriaDialog>
      </AriaModal>
    </AriaModalOverlay>
  );
};
